import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Area
} from 'recharts';
import api, { API_BASE_URL } from '../services/api';

const Research = () => {
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [industryTrends, setIndustryTrends] = useState([]);
  const [industrySummary, setIndustrySummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [sizeCategory, setSizeCategory] = useState(null); // null means all sizes
  const [selectedMetric, setSelectedMetric] = useState('costs'); // costs, participation, assets
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredIndustries, setFilteredIndustries] = useState([]);
  const navigate = useNavigate();

  const sizeCategories = [
    { value: null, label: 'All Sizes' },
    { value: 'Micro', label: 'Micro (1-9)' },
    { value: 'Small', label: 'Small (10-49)' },
    { value: 'Medium-Small', label: 'Medium-Small (50-249)' },
    { value: 'Medium', label: 'Medium (250-999)' },
    { value: 'Large', label: 'Large (1000+)' }
  ];

  // Load industries on mount
  useEffect(() => {
    loadIndustries();
  }, []);

  const loadIndustries = async () => {
    try {
      setLoading(true);
      const data = await api.getResearchIndustries();
      setIndustries(data);
      setError(null);
    } catch (err) {
      console.error('Error loading industries:', err);
      setError('Failed to load industries. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter industries based on search query
  useEffect(() => {
    const allIndustriesOption = {
      code: 'ALL',
      name: 'All Industries',
      total_plans: industries.reduce((sum, ind) => sum + (ind.total_plans || 0), 0)
    };

    if (!searchQuery.trim()) {
      // Show "All Industries" plus first 10 when empty
      setFilteredIndustries([allIndustriesOption, ...industries.slice(0, 10)]);
    } else {
      const query = searchQuery.toLowerCase();
      
      // Check if "all" matches the query
      const allMatches = 'all industries'.includes(query);
      
      // Filter regular industries
      const filtered = industries.filter(industry =>
        industry.name.toLowerCase().includes(query) ||
        String(industry.code).includes(query)  // Convert code to string for search
      ).slice(0, 10); // Limit to 10 results
      
      // Include "All Industries" if it matches or if no specific search
      if (allMatches) {
        setFilteredIndustries([allIndustriesOption, ...filtered.slice(0, 9)]);
      } else {
        setFilteredIndustries(filtered);
      }
    }
  }, [searchQuery, industries]);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowDropdown(true);
  };

  // Handle industry selection from dropdown
  const handleIndustrySelectFromDropdown = (industry) => {
    setSelectedIndustry(industry);
    setSearchQuery(industry.name);
    setShowDropdown(false);
    setSizeCategory(null); // Reset size filter
  };

  // Wrap loadIndustryData in useCallback with proper dependencies
  const loadIndustryData = useCallback(async () => {
    if (!selectedIndustry) return;
    
    console.log('=== loadIndustryData CALLBACK EXECUTED ===');
    console.log('Industry:', selectedIndustry.code, selectedIndustry.name);
    console.log('Size category:', sizeCategory);
    
    try {
      setLoading(true);
      setError(null);

      console.log('Calling API with:', {
        industry_code: selectedIndustry.code,
        start_year: 2009,
        end_year: 2024,
        size_category: sizeCategory
      });

      // Load trends data (filtered by size if applicable)
      const trendsData = await api.getIndustryTrends(selectedIndustry.code, 2009, 2024, sizeCategory);
      
      console.log('=== API RESPONSE RECEIVED ===');
      console.log('Number of years returned:', trendsData.length);
      console.log('First year data sample:', trendsData[0]);
      
      // Reverse for chronological order without mutating original
      const reversedTrends = [...trendsData].reverse();
      setIndustryTrends(reversedTrends);
      
      // Calculate summary from trends data
      // Use 2024 data from trends (which is already filtered by size)
      const data2024 = trendsData.find(d => d.filing_year === 2024);
      
      console.log('=== 2024 DATA EXTRACTED ===');
      console.log('Size category:', sizeCategory);
      console.log('Total plans:', data2024?.total_plans_filed);
      console.log('New plans:', data2024?.new_plans);
      console.log('Closed plans:', data2024?.closed_plans);
      console.log('Avg admin cost:', data2024?.avg_admin_cost_per_participant);
      
      if (data2024) {
        // Use the filtered data for summary when size is selected
        setIndustrySummary({
          industry_code: data2024.industry_code_4digit,
          industry_name: data2024.industry_name,
          latest_year: data2024.filing_year,
          total_plans_filed: data2024.total_plans_filed,
          new_plans: data2024.new_plans,
          closed_plans: data2024.closed_plans,
          churn_rate_pct: data2024.churn_rate_pct,
          avg_balance_per_participant: data2024.avg_balance_per_participant,
          avg_admin_cost_per_participant: data2024.avg_admin_cost_per_participant,
          avg_employer_contrib: data2024.avg_employer_contrib,
          avg_employee_contrib: data2024.avg_employee_contrib,
          avg_participation_rate: data2024.avg_participation_rate,
          avg_plan_age: data2024.avg_plan_age
        });
      } else {
        // Fallback: if no 2024 data, use the most recent year
        const mostRecent = trendsData[trendsData.length - 1];
        if (mostRecent) {
          setIndustrySummary({
            industry_code: mostRecent.industry_code_4digit,
            industry_name: mostRecent.industry_name,
            latest_year: mostRecent.filing_year,
            total_plans_filed: mostRecent.total_plans_filed,
            new_plans: mostRecent.new_plans,
            closed_plans: mostRecent.closed_plans,
            churn_rate_pct: mostRecent.churn_rate_pct,
            avg_balance_per_participant: mostRecent.avg_balance_per_participant,
            avg_admin_cost_per_participant: mostRecent.avg_admin_cost_per_participant,
            avg_employer_contrib: mostRecent.avg_employer_contrib,
            avg_employee_contrib: mostRecent.avg_employee_contrib,
            avg_participation_rate: mostRecent.avg_participation_rate,
            avg_plan_age: mostRecent.avg_plan_age
          });
        }
      }
    } catch (err) {
      console.error('Error loading industry data:', err);
      setError('Failed to load industry data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedIndustry, sizeCategory]); // Include dependencies

  // Load trends when industry or size changes
  useEffect(() => {
    console.log('=== useEffect [loadIndustryData] TRIGGERED ===');
    console.log('Calling loadIndustryData...');
    loadIndustryData();
  }, [loadIndustryData]); // Now we can safely depend on loadIndustryData

  const handleIndustrySelect = (industry) => {
    setSelectedIndustry(industry);
    setSizeCategory(null); // Reset size filter
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return 'N/A';
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatBasisPoints = (value) => {
    if (!value && value !== 0) return 'N/A';
    return `${value.toFixed(0)} bps`;
  };

  // Handle CSV export download
  const handleDownloadCSV = async () => {
    if (!selectedIndustry) return;
    
    try {
      setDownloading(true);
      
      // Build the URL with query parameters - use the backend server directly
      let url = `${API_BASE_URL}/research/industry-export?industry_code=${selectedIndustry.code}&year=2024`;
      if (sizeCategory) {
        url += `&size_category=${encodeURIComponent(sizeCategory)}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Failed to download CSV');
      }
      
      // Get the blob
      const blob = await response.blob();
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = downloadUrl;
      
      // Generate filename with industry code and size if applicable
      const sizeSuffix = sizeCategory ? `_${sizeCategory}` : '';
      a.download = `industry_${selectedIndustry.code}${sizeSuffix}_2024.csv`;
      
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download CSV. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
          padding: '1rem'
        }}>
          <p style={{ fontWeight: 700, color: '#111827', marginBottom: '0.5rem' }}>Year {label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ fontSize: '0.875rem', color: entry.color }}>
              {entry.name}: {entry.name.includes('$') || entry.name.includes('Cost') || entry.name.includes('Contrib')
                ? formatCurrency(entry.value)
                : entry.name.includes('Churn Rate')
                ? `${entry.value?.toFixed(1)}%`
                : entry.name.includes('%') || entry.name.includes('Rate')
                ? formatPercent(entry.value)
                : entry.name.includes('bps')
                ? formatBasisPoints(entry.value)
                : formatNumber(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ minHeight: '100vh', background: 'white' }}>
      {/* Header - Minimal & Clean */}
      <header style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '1rem 0',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div 
              style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1e3a8a', letterSpacing: '0.02em', cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              Love Your 401k
            </div>
            <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'center' }}>
              <a href="/" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Search Plans</a>
              <a href="/estimator" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>What Should I Pay for a 401(k)?</a>
              <a href="/admin/research" style={{ color: '#1e3a8a', textDecoration: 'none', fontSize: '0.9375rem', fontWeight: 600 }}>401(k) Research</a>
              <a href="/admin/prospecting" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Prospecting</a>
              <a href="/admin/blog-writer" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Blog Writer</a>
              <a href="/admin/cohort" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Cohort Analysis</a>
              <a href="/faq" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>FAQ</a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section style={{ padding: '3rem 0 2rem 0', background: 'white' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: 900,
              color: '#111827',
              marginBottom: '1rem',
              lineHeight: 1.1,
              letterSpacing: '-0.02em'
            }}>
              Industry Research & 401(k) Trends
            </h1>
            <p style={{
              fontSize: '1.125rem',
              color: '#6b7280',
              marginBottom: '2rem',
              lineHeight: 1.6
            }}>
              Explore comprehensive industry-level data spanning 2009-{new Date().getFullYear() - 1}
            </p>

            {/* Industry Search */}
            <div style={{ position: 'relative', maxWidth: '600px', margin: '0 auto' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                placeholder="Search industries..."
                style={{
                  width: '100%',
                  padding: '1rem 1.5rem',
                  fontSize: '1rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                }}
              />

              {showDropdown && filteredIndustries.length > 0 && (
                <div style={{
                  position: 'absolute',
                  width: '100%',
                  marginTop: '0.5rem',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  zIndex: 50
                }}>
                  {filteredIndustries.map((industry, index) => (
                    <div
                      key={industry.code}
                      onClick={() => handleIndustrySelectFromDropdown(industry)}
                      style={{
                        padding: '1rem 1.5rem',
                        cursor: 'pointer',
                        borderBottom: index < filteredIndustries.length - 1 ? '1px solid #f3f4f6' : 'none',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#111827', marginBottom: '0.25rem' }}>
                        {industry.name}
                      </div>
                      <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                        Code: {industry.code} • {formatNumber(industry.total_plans)} plans
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Controls Section */}
      {selectedIndustry && (
        <section style={{ padding: '2rem 0', background: '#fafbfc', borderTop: '1px solid #e5e7eb' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              {/* Size Filter */}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {sizeCategories.map(cat => (
                  <button
                    key={cat.value || 'all'}
                    onClick={() => setSizeCategory(cat.value)}
                    style={{
                      padding: '0.5rem 1rem',
                      border: sizeCategory === cat.value ? '2px solid #1e3a8a' : '1px solid #e5e7eb',
                      borderRadius: '8px',
                      background: sizeCategory === cat.value ? '#eff6ff' : 'white',
                      color: sizeCategory === cat.value ? '#1e3a8a' : '#6b7280',
                      fontSize: '0.875rem',
                      fontWeight: sizeCategory === cat.value ? 600 : 400,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              {/* Metric Selector */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setSelectedMetric('costs')}
                  style={{
                    padding: '0.5rem 1rem',
                    border: selectedMetric === 'costs' ? '2px solid #1e3a8a' : '1px solid #e5e7eb',
                    borderRadius: '8px',
                    background: selectedMetric === 'costs' ? '#eff6ff' : 'white',
                    color: selectedMetric === 'costs' ? '#1e3a8a' : '#6b7280',
                    fontSize: '0.875rem',
                    fontWeight: selectedMetric === 'costs' ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Costs & Fees
                </button>
                <button
                  onClick={() => setSelectedMetric('participation')}
                  style={{
                    padding: '0.5rem 1rem',
                    border: selectedMetric === 'participation' ? '2px solid #1e3a8a' : '1px solid #e5e7eb',
                    borderRadius: '8px',
                    background: selectedMetric === 'participation' ? '#eff6ff' : 'white',
                    color: selectedMetric === 'participation' ? '#1e3a8a' : '#6b7280',
                    fontSize: '0.875rem',
                    fontWeight: selectedMetric === 'participation' ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Participation
                </button>
                <button
                  onClick={() => setSelectedMetric('assets')}
                  style={{
                    padding: '0.5rem 1rem',
                    border: selectedMetric === 'assets' ? '2px solid #1e3a8a' : '1px solid #e5e7eb',
                    borderRadius: '8px',
                    background: selectedMetric === 'assets' ? '#eff6ff' : 'white',
                    color: selectedMetric === 'assets' ? '#1e3a8a' : '#6b7280',
                    fontSize: '0.875rem',
                    fontWeight: selectedMetric === 'assets' ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Assets & Plans
                </button>
              </div>

              {/* Download Button */}
              <button
                onClick={handleDownloadCSV}
                disabled={downloading}
                style={{
                  padding: '0.5rem 1.5rem',
                  background: '#1e3a8a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: downloading ? 'not-allowed' : 'pointer',
                  opacity: downloading ? 0.5 : 1,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => !downloading && (e.currentTarget.style.background = '#1e40af')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#1e3a8a')}
              >
                {downloading ? 'Downloading...' : 'Download CSV'}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Summary Stats */}
      {industrySummary && !loading && (
        <section style={{ padding: '2rem 0', background: 'white' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '1.5rem' 
            }}>
              {/* Total Plans */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
              }}>
                <div style={{ fontSize: '0.6875rem', color: '#6b7280', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Total Plans
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
                  {formatNumber(industrySummary.total_plans_filed)}
                </div>
              </div>

              {/* Avg Admin Cost */}
              <div style={{
                background: '#eff6ff',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid #dbeafe'
              }}>
                <div style={{ fontSize: '0.6875rem', color: '#1e3a8a', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Avg Admin Cost
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e3a8a' }}>
                  {formatCurrency(industrySummary.avg_admin_cost_per_participant)}/participant
                </div>
              </div>

              {/* Avg Participation */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
              }}>
                <div style={{ fontSize: '0.6875rem', color: '#6b7280', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Avg Participation
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
                  {formatPercent(industrySummary.avg_participation_rate)}
                </div>
              </div>

              {/* Avg Balance */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
              }}>
                <div style={{ fontSize: '0.6875rem', color: '#6b7280', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Avg Balance
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
                  {formatCurrency(industrySummary.avg_balance_per_participant)}
                </div>
              </div>

              {/* Churn Rate */}
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
              }}>
                <div style={{ fontSize: '0.6875rem', color: '#6b7280', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                  Churn Rate
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
                  {industrySummary.churn_rate_pct?.toFixed(1)}%
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Charts Section */}
      {selectedIndustry && industryTrends.length > 0 && !loading && (
        <section style={{ padding: '2rem 0', background: '#fafbfc' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
            <div style={{ display: 'grid', gap: '2rem' }}>
              
              {/* Cost Charts */}
              {selectedMetric === 'costs' && (
                <>
                  {/* Admin Cost Per Participant */}
                  <div style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '2rem',
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                  }}>
                    <h4 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1.5rem' }}>
                      Average Admin Cost Per Participant
                    </h4>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={industryTrends}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis 
                          dataKey="filing_year" 
                          stroke="#737373"
                          style={{ fontSize: '14px' }}
                        />
                        <YAxis 
                          stroke="#737373"
                          style={{ fontSize: '14px' }}
                          tickFormatter={(value) => `$${value}`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="avg_admin_cost_per_participant"
                          stroke="#1e3a8a"
                          strokeWidth={3}
                          name="Cost Per Participant ($)"
                          dot={{ fill: '#1e3a8a', r: 5 }}
                          activeDot={{ r: 7 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Contribution Analysis */}
                  <div style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '2rem',
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                  }}>
                    <h4 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1.5rem' }}>
                      Average Contributions Per Participant
                    </h4>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={industryTrends}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis 
                          dataKey="filing_year" 
                          stroke="#737373"
                          style={{ fontSize: '14px' }}
                        />
                        <YAxis 
                          stroke="#737373"
                          style={{ fontSize: '14px' }}
                          tickFormatter={(value) => `$${value}`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar
                          dataKey="avg_employer_contrib"
                          fill="#1e3a8a"
                          name="Employer Contrib ($)"
                        />
                        <Bar
                          dataKey="avg_employee_contrib"
                          fill="#60a5fa"
                          name="Employee Contrib ($)"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}

              {/* Participation Charts */}
              {selectedMetric === 'participation' && (
                <>
                  {/* Participation Rate */}
                  <div style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '2rem',
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                  }}>
                    <h4 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1.5rem' }}>
                      Average Participation Rate
                    </h4>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={industryTrends}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis 
                          dataKey="filing_year" 
                          stroke="#737373"
                          style={{ fontSize: '14px' }}
                        />
                        <YAxis 
                          stroke="#737373"
                          style={{ fontSize: '14px' }}
                          tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="avg_participation_rate"
                          stroke="#10b981"
                          strokeWidth={3}
                          name="Participation Rate (%)"
                          dot={{ fill: '#10b981', r: 5 }}
                          activeDot={{ r: 7 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Account Balance */}
                  <div style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '2rem',
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                  }}>
                    <h4 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1.5rem' }}>
                      Average Account Balance Per Participant
                    </h4>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={industryTrends}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis 
                          dataKey="filing_year" 
                          stroke="#737373"
                          style={{ fontSize: '14px' }}
                        />
                        <YAxis 
                          stroke="#737373"
                          style={{ fontSize: '14px' }}
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="avg_balance_per_participant"
                          stroke="#1e3a8a"
                          strokeWidth={3}
                          name="Avg Balance ($)"
                          dot={{ fill: '#1e3a8a', r: 5 }}
                          activeDot={{ r: 7 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}

              {/* Asset Charts */}
              {selectedMetric === 'assets' && (
                <>
                  {/* Total Assets */}
                  <div style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '2rem',
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                  }}>
                    <h4 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1.5rem' }}>
                      Total Industry Assets
                    </h4>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={industryTrends}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis 
                          dataKey="filing_year" 
                          stroke="#737373"
                          style={{ fontSize: '14px' }}
                        />
                        <YAxis 
                          stroke="#737373"
                          style={{ fontSize: '14px' }}
                          tickFormatter={(value) => `$${(value / 1e9).toFixed(1)}B`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar
                          dataKey="total_industry_assets"
                          fill="#1e3a8a"
                          name="Total Assets ($)"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Plan Activity */}
                  <div style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '2rem',
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                  }}>
                    <h4 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1.5rem' }}>
                      Plan Activity Over Time
                    </h4>
                    <ResponsiveContainer width="100%" height={350}>
                      <ComposedChart data={industryTrends}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis 
                          dataKey="filing_year" 
                          stroke="#737373"
                          style={{ fontSize: '14px' }}
                        />
                        <YAxis 
                          stroke="#737373"
                          style={{ fontSize: '14px' }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar
                          dataKey="new_plans"
                          fill="#10b981"
                          name="New Plans"
                        />
                        <Bar
                          dataKey="closed_plans"
                          fill="#ef4444"
                          name="Closed Plans"
                        />
                        <Line
                          type="monotone"
                          dataKey="total_plans_filed"
                          stroke="#1e3a8a"
                          strokeWidth={3}
                          name="Total Plans"
                          dot={{ fill: '#1e3a8a', r: 5 }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Churn Rate */}
                  <div style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '2rem',
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                  }}>
                    <h4 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1.5rem' }}>
                      Plan Churn Rate
                    </h4>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={industryTrends}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis 
                          dataKey="filing_year" 
                          stroke="#737373"
                          style={{ fontSize: '14px' }}
                        />
                        <YAxis 
                          stroke="#737373"
                          style={{ fontSize: '14px' }}
                          tickFormatter={(value) => `${value.toFixed(1)}%`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="churn_rate_pct"
                          stroke="#ef4444"
                          strokeWidth={3}
                          name="Churn Rate (%)"
                          dot={{ fill: '#ef4444', r: 5 }}
                          activeDot={{ r: 7 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Participants with Balance */}
                  <div style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '2rem',
                    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                  }}>
                    <h4 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1.5rem' }}>
                      Total Participants with Account Balance
                    </h4>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={industryTrends}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis 
                          dataKey="filing_year" 
                          stroke="#737373"
                          style={{ fontSize: '14px' }}
                        />
                        <YAxis 
                          stroke="#737373"
                          style={{ fontSize: '14px' }}
                          tickFormatter={(value) => `${(value / 1e6).toFixed(1)}M`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar
                          dataKey="total_participants_with_balance"
                          fill="#3b82f6"
                          name="Participants"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Empty State */}
      {!selectedIndustry && !loading && (
        <section style={{ padding: '4rem 0', background: 'white' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 2.5rem', textAlign: 'center' }}>
            <div style={{
              width: '96px',
              height: '96px',
              margin: '0 auto 1.5rem',
              background: '#eff6ff',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg
                style={{ width: '48px', height: '48px', color: '#1e3a8a' }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>
              Select an Industry to Begin
            </h3>
            <p style={{ fontSize: '1rem', color: '#6b7280', lineHeight: 1.6 }}>
              Choose an industry from the search above to explore comprehensive 401(k) trends, 
              cost analysis, and performance metrics spanning 2009-{new Date().getFullYear() - 1}.
            </p>
          </div>
        </section>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem 0' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #1e3a8a',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite'
          }}></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 2.5rem' }}>
          <div style={{
            padding: '1.5rem',
            background: '#fee2e2',
            borderLeft: '4px solid #ef4444',
            borderRadius: '8px'
          }}>
            <p style={{ color: '#991b1b', fontWeight: 500 }}>{error}</p>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer style={{ background: '#111827', color: 'white', padding: '2rem 0', marginTop: '3rem' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2.5rem' }}>
          <div style={{ textAlign: 'center' }}>
            <h4 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>Love Your 401k</h4>
            <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
              Powered by Form 5500 data • Helping employees make better retirement decisions
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Research;
